import React, { useState } from 'react';
import { apiFetch } from './utils/api';

function SettingsImportData() {
  const [importType, setImportType] = useState('customers'); // 'customers', 'sites', 'suppliers', 'materials', 'jobs'
  const [selectedFile, setSelectedFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState([]);
  const [duplicateAction, setDuplicateAction] = useState('skip');
  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [results, setResults] = useState(null);

  // Template Import/Export State
  const [templateFile, setTemplateFile] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [templateImporting, setTemplateImporting] = useState(false);
  const [templateExporting, setTemplateExporting] = useState(false);
  const [templateResults, setTemplateResults] = useState(null);
  const [exportCategory, setExportCategory] = useState('all'); // 'all', 'my', 'sample'
  const [exportFileName, setExportFileName] = useState('');
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const templateResultErrors = Array.isArray(templateResults?.errors) ? templateResults.errors : [];

  const normalizeImportResults = (raw = {}) => {
    const created = Number(raw.created ?? raw.imported ?? 0);
    const updated = Number(raw.updated ?? 0);
    const skipped = Number(raw.skipped ?? 0);
    const errors = Array.isArray(raw.errors) ? raw.errors : [];

    return {
      ...raw,
      total: Number(raw.total ?? created + updated + skipped + errors.length),
      created,
      updated,
      skipped,
      errors
    };
  };

  const normalizedResults = results ? normalizeImportResults(results) : null;
  const resultErrors = normalizedResults?.errors || [];

  const syncFromGoogleSheets = async () => {
    if (!window.confirm('Sync products from Google Sheets? This will update existing products with matching codes.')) {
      return;
    }
    
    setSyncing(true);
    try {
      const response = await apiFetch('/api/import/products-google', {
        method: 'POST'
      });
      
      const result = await response.json();
      const normalized = normalizeImportResults(result);
      
      if (result.success) {
        alert(`Google Sheets sync complete!\n\nImported: ${normalized.created}\nUpdated: ${normalized.updated}\nSkipped: ${normalized.skipped}`);
        setResults(normalized);
      } else {
        alert('Sync failed: ' + result.error);
      }
    } catch (error) {
      console.error('Error syncing from Google Sheets:', error);
      alert('Failed to sync from Google Sheets: ' + error.message);
    }
    setSyncing(false);
  };

  // Load templates for selection
  const loadTemplatesForExport = async (category) => {
    setLoadingTemplates(true);
    setSelectedTemplates([]);
    try {
      const url = category === 'all'
        ? '/api/templates-io/export'
        : `/api/templates-io/export?area=${category}`;
      
      const response = await apiFetch(url);
      const data = await response.json();
      
      if (data.success) {
        setAvailableTemplates(data.templates);
      } else {
        alert('Failed to load templates: ' + data.error);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      alert('Failed to load templates: ' + error.message);
    }
    setLoadingTemplates(false);
  };

  // Handle category change
  const handleCategoryChange = (category) => {
    setExportCategory(category);
    loadTemplatesForExport(category);
  };

  // Toggle template selection
  const toggleTemplateSelection = (index) => {
    if (selectedTemplates.includes(index)) {
      setSelectedTemplates(selectedTemplates.filter(i => i !== index));
    } else {
      setSelectedTemplates([...selectedTemplates, index]);
    }

    // Auto-populate filename from first selected template
    if (!exportFileName && selectedTemplates.length === 0) {
      const template = availableTemplates[index];
      if (template && template.name) {
        // Sanitize template name for filename
        const sanitized = template.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        setExportFileName(sanitized);
      }
    }
  };

  // Select all templates
  const selectAllTemplates = () => {
    if (selectedTemplates.length === availableTemplates.length) {
      setSelectedTemplates([]);
    } else {
      setSelectedTemplates(availableTemplates.map((_, i) => i));
    }
  };

  // Template Export
  const handleTemplateExport = async () => {
    if (selectedTemplates.length === 0) {
      alert('Please select at least one template to export');
      return;
    }

    if (!exportFileName.trim()) {
      alert('Please enter a filename for the export');
      return;
    }

    setTemplateExporting(true);
    try {
      // Get only selected templates
      const templatesToExport = selectedTemplates.map(i => availableTemplates[i]);
      
      // Download JSON file
      const blob = new Blob([JSON.stringify(templatesToExport, null, 2)], { type: 'application/json' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      // Use user's filename (sanitize and add extension if needed)
      const sanitizedName = exportFileName.trim().replace(/\.json$/i, '');
      a.download = `${sanitizedName}.json`;
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      
      alert(`Exported ${templatesToExport.length} template(s) to ${sanitizedName}.json`);
    } catch (error) {
      console.error('Error exporting templates:', error);
      alert('Failed to export templates: ' + error.message);
    }
    setTemplateExporting(false);
  };

  // Template Import State
  const [importLibraryLocation, setImportLibraryLocation] = useState(''); // Library folder (all, my, sample)
  const [importArea, setImportArea] = useState(''); // Area in DoorBase (sales_order, job, invoice, etc.)
  const [importFileName, setImportFileName] = useState('');

  // Template File Upload
  const handleTemplateFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setTemplateFile(file);
    setTemplateResults(null);
    
    // Auto-populate filename from uploaded file (remove .json extension)
    const fileName = file.name.replace(/\.json$/i, '');
    setImportFileName(fileName);
    
    // Parse JSON for preview
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target.result);
        if (!Array.isArray(json)) {
          alert('Invalid template file: must be an array of templates');
          return;
        }
        setTemplatePreview(json);
        
        // Auto-detect category from first template with an area
        const templateWithArea = json.find(tpl => tpl.area);
        if (templateWithArea && templateWithArea.area) {
          setImportArea(templateWithArea.area);
        }
      } catch (err) {
        alert('Invalid JSON file: ' + err.message);
      }
    };
    reader.readAsText(file);
  };

  // Template Import
  const handleTemplateImport = async () => {
    console.log('🔵 handleTemplateImport called');
    console.log('Library location:', importLibraryLocation);
    console.log('Area:', importArea);
    console.log('Filename:', importFileName);
    console.log('Templates to import:', templatePreview?.length);
    
    if (!importLibraryLocation) {
      alert('Please select a library location (where to save the templates)');
      return;
    }

    if (!importArea) {
      alert('Please select an area (where the template will be used in DoorBase)');
      return;
    }

    if (!importFileName.trim()) {
      alert('Please enter a file name');
      return;
    }

    if (!templateFile || !templatePreview) {
      alert('Please select a template file first');
      return;
    }

    const areaLabel = importArea === 'sales_order' ? 'Sales Orders' : 
                      importArea === 'job' ? 'Jobs' : 
                      importArea === 'invoice' ? 'Invoices' :
                      importArea === 'quote' ? 'Quotes' :
                      importArea === 'purchase_order' ? 'Purchase Orders' :
                      importArea === 'ppm' ? 'PPM' :
                      importArea;

    const libraryLabel = importLibraryLocation === 'all' ? 'All Templates' :
                         importLibraryLocation === 'my' ? 'My Templates' :
                         'Sample Templates';

    if (!window.confirm(`Import ${templatePreview.length} template(s) to ${libraryLabel} for ${areaLabel}?`)) {
      return;
    }

    setTemplateImporting(true);
    try {
      // Determine template_type based on library location
      let templateType = 'layout';
      if (importLibraryLocation === 'sample') {
        templateType = 'sample';
      } else if (importLibraryLocation === 'my') {
        templateType = 'layout';
      }

      // Update templates with selected area, type, AND filename
      const templatesToImport = templatePreview.map(tpl => {
        const updated = { ...tpl };
        updated.name = importFileName.trim(); // Use the filename entered by user
        updated.area = importArea; // Set the area where template will be used
        updated.template_type = templateType; // Set based on library location
        return updated;
      });

      const response = await apiFetch('/api/templates-io/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          templates: templatesToImport,
          fileName: importFileName.trim()
        })
      });

      const result = await response.json();
      setTemplateResults(result);
      
      if (result.success) {
        alert(`Import complete!\n\nCreated: ${result.created}\nUpdated: ${result.updated}\nSkipped: ${result.skipped}`);
      } else {
        alert('Import failed: ' + result.error);
      }
    } catch (err) {
      console.error('Error importing templates:', err);
      alert('Error importing templates: ' + err.message);
    }
    setTemplateImporting(false);
  };

  // CSV Templates
  const customerTemplate = `company_name,contact_name,phone,mobile,email,address,city,county,postcode,customer_type
Example Ltd,John Smith,01234567890,07700900123,john@example.com,123 High Street,London,Greater London,SW1A 1AA,commercial`;

  const siteTemplate = `customer_name,site_name,contact_name,phone,mobile,email,address,city,county,postcode,site_type
Example Ltd,Main Site,Jane Doe,01234567890,07700900456,jane@example.com,456 Business Park,Manchester,Greater Manchester,M1 1AA,head_office`;

  const supplierTemplate = `company_name,contact_name,phone,mobile,email,address,city,county,postcode,supplier_type,account_number
Acme Doors Ltd,Bob Johnson,01234567890,07700900789,bob@acmedoors.com,789 Industrial Estate,Birmingham,West Midlands,B1 1BB,door_manufacturer,SUP001`;

  const materialTemplate = `part_number,description,category,unit_price,stock_level
DOOR-001,Standard Fire Door,Doors,450.00,25
HINGE-001,Heavy Duty Hinge Set,Hardware,12.50,150`;

  const jobTemplate = `job_number,site_address,job_status,job_category,po_number,job_description,site_contact,site_number,opening_times,specific_requirements\nJOB-2024-001,123 Main St,New,installation,PO-001,Install door,John Doe,01234567890,Mon-Fri 9am-5pm,PPE required`;

  const invoiceTemplate = `invoice_ref,customer_id,site_id,invoice_date,due_date,status,notes,payment_terms,subtotal,vat_amount,total,paid_amount
INV-2024-001,1,1,2026-06-09,2026-07-09,draft,Imported invoice,Payment due within 30 days,100.00,20.00,120.00,0.00`;

  const invoiceItemsTemplate = `invoice_ref,line_number,description,category,quantity,unit,unit_price
INV-2024-001,1,Service call,labour,1,unit,100.00`;

  const invoicePaymentsTemplate = `invoice_ref,payment_date,amount,payment_method,reference,notes
INV-2024-001,2026-06-09,120.00,BACS,BANK-REF-001,Imported payment`;

  const quoteTemplate = `quote_ref,customer_id,site_id,quote_date,valid_until,status,notes,terms,subtotal,vat_amount,total,total_cost,total_profit,profit_margin
QT-2024-001,1,1,2026-06-09,2026-07-09,draft,Imported quote,Payment due within 30 days. All prices exclude VAT.,100.00,20.00,120.00,70.00,30.00,30`;

  const quoteItemsTemplate = `quote_ref,line_number,description,category,quantity,unit,cost_price,sell_price
QT-2024-001,1,Door service,labour,1,unit,70.00,100.00`;

  const productTemplate = `Product Name,Product Code,Type,Product Category,Product Description,Customer,Cost Price,Markup,Price,Sales Tax Rate,Nominal Code,Sales Account Code,Purchase Account Code,Purchase Tax Rate,Location,Barcode
Standard Door,DOOR-001,Product,Doors,Standard commercial door,,300.00,50,450.00,20% VAT,4000,4000,5000,20% VAT,Main Warehouse,1234567890123`;

  const downloadTemplate = (type) => {
    let template, filename;
    switch(type) {
      case 'customers':
        template = customerTemplate;
        filename = 'customers_template.csv';
        break;
      case 'sites':
        template = siteTemplate;
        filename = 'sites_template.csv';
        break;
      case 'suppliers':
        template = supplierTemplate;
        filename = 'suppliers_template.csv';
        break;
      case 'materials':
        template = materialTemplate;
        filename = 'materials_template.csv';
        break;
      case 'jobs':
        template = jobTemplate;
        filename = 'jobs_template.csv';
        break;
      case 'invoices':
        template = invoiceTemplate;
        filename = 'invoices_template.csv';
        break;
      case 'invoice-items':
        template = invoiceItemsTemplate;
        filename = 'invoice_items_template.csv';
        break;
      case 'invoice-payments':
        template = invoicePaymentsTemplate;
        filename = 'invoice_payments_template.csv';
        break;
      case 'quotes':
        template = quoteTemplate;
        filename = 'quotes_template.csv';
        break;
      case 'quote-items':
        template = quoteItemsTemplate;
        filename = 'quote_items_template.csv';
        break;
      case 'products':
        template = productTemplate;
        filename = 'products_template.csv';
        break;
      default:
        template = customerTemplate;
        filename = 'template.csv';
    }
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setSelectedFile(file);
    setResults(null);
    
    // Parse CSV for preview
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.trim().split('\n');
      if (lines.length < 2) {
        alert('CSV file is empty or invalid');
        return;
      }
      
      const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
      const preview = [];
      
      for (let i = 1; i < Math.min(6, lines.length); i++) {
        const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        preview.push(row);
      }
      
      setCsvPreview(preview);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    setImporting(true);
    try {
      let endpoint;
      switch(importType) {
        case 'customers':
          endpoint = '/api/import/customers';
          break;
        case 'sites':
          endpoint = '/api/import/sites';
          break;
        case 'suppliers':
          endpoint = '/api/import/suppliers';
          break;
        case 'materials':
          endpoint = '/api/import/materials';
          break;
        case 'jobs':
          endpoint = '/api/import/jobs';
          break;
        case 'invoices':
          endpoint = '/api/import/invoices';
          break;
        case 'invoice-items':
          endpoint = '/api/import/invoice-items';
          break;
        case 'invoice-payments':
          endpoint = '/api/import/invoice-payments';
          break;
        case 'quotes':
          endpoint = '/api/import/quotes';
          break;
        case 'quote-items':
          endpoint = '/api/import/quote-items';
          break;
        case 'products':
          endpoint = '/api/import/products';
          break;
        default:
          endpoint = '/api/import/customers';
      }

      // Use FormData for file upload
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('duplicateAction', duplicateAction);

      const response = await apiFetch(endpoint, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();
      const normalized = normalizeImportResults(result);
      setResults(normalized);
      
      if (result.success || normalized.created > 0 || normalized.updated > 0) {
        alert(`Import complete! Imported: ${normalized.created}, Updated: ${normalized.updated}, Skipped: ${normalized.skipped}`);
      }
    } catch (err) {
      console.error('Error importing:', err);
      alert('Error importing data: ' + err.message);
    }
    setImporting(false);
  };

  return (
    <div className="bg-white rounded-lg shadow">
      
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Import & Export Data</h2>
        <p className="text-gray-600">
          Bulk import/export customers, templates, and other data.
        </p>
      </div>

      {/* ========== TEMPLATES SECTION ========== */}
      <div className="mb-8 p-6 bg-white border border-gray-300 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">📄 Templates Import/Export</h3>
        <p className="text-sm text-gray-600 mb-6">
          Export templates for backup or sharing, or import templates from another DoorBase installation.
        </p>

        {/* Export Section */}
        <div className="bg-white rounded-lg border border-gray-300 p-4 mb-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Export Templates</h4>
          
          {/* Step 1: Select Category */}
          <div className="mb-4">
            <label className="text-sm text-gray-700 mb-2 block">Step 1: Select category</label>
            <div className="flex gap-3">
              <button
                onClick={() => handleCategoryChange('all')}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  exportCategory === 'all'
                    ? 'bg-blue-100 text-blue-700 border-2 border-blue-400'
                    : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                }`}
              >
                📚 All Templates
              </button>
              <button
                onClick={() => handleCategoryChange('my')}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  exportCategory === 'my'
                    ? 'bg-blue-100 text-blue-700 border-2 border-blue-400'
                    : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                }`}
              >
                📁 My Templates
              </button>
              <button
                onClick={() => handleCategoryChange('sample')}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  exportCategory === 'sample'
                    ? 'bg-blue-100 text-blue-700 border-2 border-blue-400'
                    : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                }`}
              >
                ✨ Sample Templates
              </button>
            </div>
          </div>

          {/* Step 2: Select Templates */}
          {availableTemplates.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-gray-700">Step 2: Select templates to export</label>
                <button
                  onClick={selectAllTemplates}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {selectedTemplates.length === availableTemplates.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded p-3 max-h-64 overflow-y-auto">
                {loadingTemplates ? (
                  <p className="text-sm text-gray-500">Loading templates...</p>
                ) : (
                  <div className="space-y-2">
                    {availableTemplates.map((tpl, idx) => (
                      <label key={idx} className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedTemplates.includes(idx)}
                          onChange={() => toggleTemplateSelection(idx)}
                          className="w-4 h-4"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm">{tpl.name}</div>
                          {tpl.area && (
                            <span className="text-xs text-gray-500">{tpl.area}</span>
                          )}
                        </div>
                        <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded text-xs">
                          {tpl.template_type}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {selectedTemplates.length} of {availableTemplates.length} template(s) selected
              </p>
            </div>
          )}

          {/* Step 3: Filename */}
          {selectedTemplates.length > 0 && (
            <div className="mb-4">
              <label className="text-sm text-gray-700 mb-2 block">Filename</label>
              <input
                type="text"
                value={exportFileName}
                onChange={(e) => setExportFileName(e.target.value)}
                placeholder="e.g., my-templates"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                File will be saved as "{exportFileName || 'filename'}.json"
              </p>
            </div>
          )}

          {/* Step 4: Export Button */}
          {selectedTemplates.length > 0 && exportFileName && (
            <div className="flex justify-end">
              <button
                onClick={handleTemplateExport}
                disabled={templateExporting}
                className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition disabled:opacity-50"
              >
                {templateExporting ? '⏳ Exporting...' : `📥 Export ${selectedTemplates.length} Template(s)`}
              </button>
            </div>
          )}

          {availableTemplates.length === 0 && !loadingTemplates && (
            <p className="text-sm text-gray-500 italic">
              Select a category above to view available templates
            </p>
          )}
        </div>

        {/* Import Section */}
        <div className="bg-white rounded-lg border border-gray-300 p-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Import Templates</h4>
          
          {/* Step 1: File Location (File Picker) */}
          <div className="mb-4">
            <label className="text-sm text-gray-700 mb-2 block">File Location</label>
            <input
              type="file"
              accept=".json"
              onChange={handleTemplateFileUpload}
              className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none p-2"
            />
            <p className="text-xs text-gray-500 mt-1">Browse your computer for the template JSON file</p>
          </div>

          {/* Step 2: Library Location (appears after file selected) */}
          {templateFile && (
            <div className="mb-4">
              <label className="text-sm text-gray-700 mb-2 block">Library Location</label>
              <div className="flex gap-3">
                <button
                  onClick={() => setImportLibraryLocation('all')}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    importLibraryLocation === 'all'
                      ? 'bg-blue-100 text-blue-700 border-2 border-blue-400'
                      : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                  }`}
                >
                  📚 All Templates
                </button>
                <button
                  onClick={() => setImportLibraryLocation('my')}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    importLibraryLocation === 'my'
                      ? 'bg-blue-100 text-blue-700 border-2 border-blue-400'
                      : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                  }`}
                >
                  📁 My Templates
                </button>
                <button
                  onClick={() => setImportLibraryLocation('sample')}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    importLibraryLocation === 'sample'
                      ? 'bg-blue-100 text-blue-700 border-2 border-blue-400'
                      : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                  }`}
                >
                  ✨ Sample Templates
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Which library folder to save templates in</p>
            </div>
          )}

          {/* Step 3: Category/Area (appears after library location selected) */}
          {templateFile && importLibraryLocation && (
            <div className="mb-4">
              <label className="text-sm text-gray-700 mb-2 block">Category</label>
              <select
                value={importArea}
                onChange={(e) => setImportArea(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">Select where this template will be used...</option>
                <option value="sales_order">Sales Orders</option>
                <option value="job">Jobs</option>
                <option value="invoice">Invoices</option>
                <option value="quote">Quotes</option>
                <option value="purchase_order">Purchase Orders</option>
                <option value="ppm">PPM Contracts</option>
                <option value="email">Email</option>
                <option value="sms">SMS</option>
                <option value="general">General</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Which area of DoorBase will use this template
              </p>
            </div>
          )}

          {/* Step 4: File Name (appears after category selected, auto-populated) */}
          {templateFile && importLibraryLocation && importArea && (
            <div className="mb-4">
              <label className="text-sm text-gray-700 mb-2 block">Filename</label>
              <input
                type="text"
                value={importFileName}
                onChange={(e) => setImportFileName(e.target.value)}
                placeholder="e.g., My Custom Templates"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Auto-filled from file, but you can change it
              </p>
            </div>
          )}

          {templateFile && (
            <p className="text-sm text-green-600 mb-3">
              ✓ File selected: {templateFile.name}
            </p>
          )}

          {/* Template Preview */}
          {templatePreview && templatePreview.length > 0 && (
            <div className="mb-3">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Preview: {templatePreview.length} template(s) found
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded p-3 max-h-48 overflow-y-auto">
                <ul className="space-y-2 text-sm">
                  {templatePreview.map((tpl, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <span className="font-medium">{tpl.name}</span>
                      {tpl.area && (
                        <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs">
                          {tpl.area}
                        </span>
                      )}
                      {tpl.template_type && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                          {tpl.template_type}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Import Button */}
          {templatePreview && importLibraryLocation && importArea && importFileName && (
            <div className="flex justify-end">
              <button
                onClick={handleTemplateImport}
                disabled={templateImporting}
                className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition disabled:opacity-50"
              >
                {templateImporting ? '⏳ Importing...' : `📤 Import ${templatePreview.length} Template(s)`}
              </button>
            </div>
          )}

          {/* Import Results */}
          {templateResults && (
            <div className={`mt-4 rounded-lg border p-4 ${
              templateResultErrors.length > 0 
                ? 'bg-yellow-50 border-yellow-300' 
                : 'bg-green-50 border-green-300'
            }`}>
              <h5 className="font-semibold mb-2">Import Results</h5>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-gray-600">Created</div>
                  <div className="text-xl font-bold text-green-600">{templateResults.created || 0}</div>
                </div>
                <div>
                  <div className="text-gray-600">Updated</div>
                  <div className="text-xl font-bold text-blue-600">{templateResults.updated || 0}</div>
                </div>
                <div>
                  <div className="text-gray-600">Skipped</div>
                  <div className="text-xl font-bold text-gray-600">{templateResults.skipped || 0}</div>
                </div>
              </div>
              {templateResultErrors.length > 0 && (
                <div className="mt-3">
                  <h6 className="font-semibold text-red-700 mb-1">Errors:</h6>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {templateResultErrors.map((err, i) => (
                      <div key={i} className="text-xs bg-white p-2 rounded border border-red-200">
                        {typeof err === 'string' ? err : err.error || JSON.stringify(err)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ========== CSV IMPORT SECTION ========== */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 CSV Bulk Import</h3>
      </div>

      {/* Import Type Selection */}
      <div className="bg-white rounded-lg border border-gray-300 p-6 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Select Import Type</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => { setImportType('customers'); setCsvPreview([]); setResults(null); }}
            className={`px-6 py-3 rounded-lg font-medium transition ${
              importType === 'customers'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            👥 Customers
          </button>
          <button
            onClick={() => { setImportType('sites'); setCsvPreview([]); setResults(null); }}
            className={`px-6 py-3 rounded-lg font-medium transition ${
              importType === 'sites'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            📍 Sites
          </button>
          <button
            onClick={() => { setImportType('suppliers'); setCsvPreview([]); setResults(null); }}
            className={`px-6 py-3 rounded-lg font-medium transition ${
              importType === 'suppliers'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            🏭 Suppliers
          </button>
          <button
            onClick={() => { setImportType('materials'); setCsvPreview([]); setResults(null); }}
            className={`px-6 py-3 rounded-lg font-medium transition ${
              importType === 'materials'
                ? 'bg-orange-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            📦 Materials
          </button>
          <button
            onClick={() => { setImportType('jobs'); setCsvPreview([]); setResults(null); }}
            className={`px-6 py-3 rounded-lg font-medium transition ${
              importType === 'jobs'
                ? 'bg-orange-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            📋 Jobs
          </button>
          <button
            onClick={() => { setImportType('invoices'); setCsvPreview([]); setResults(null); }}
            className={`px-6 py-3 rounded-lg font-medium transition ${
              importType === 'invoices'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            💳 Invoices
          </button>
          <button
            onClick={() => { setImportType('invoice-items'); setCsvPreview([]); setResults(null); }}
            className={`px-6 py-3 rounded-lg font-medium transition ${
              importType === 'invoice-items'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            📝 Invoice Items
          </button>
          <button
            onClick={() => { setImportType('invoice-payments'); setCsvPreview([]); setResults(null); }}
            className={`px-6 py-3 rounded-lg font-medium transition ${
              importType === 'invoice-payments'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            💰 Invoice Payments
          </button>
          <button
            onClick={() => { setImportType('quotes'); setCsvPreview([]); setResults(null); }}
            className={`px-6 py-3 rounded-lg font-medium transition ${
              importType === 'quotes'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            💜 Quotes
          </button>
          <button
            onClick={() => { setImportType('quote-items'); setCsvPreview([]); setResults(null); }}
            className={`px-6 py-3 rounded-lg font-medium transition ${
              importType === 'quote-items'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            📋 Quote Items
          </button>
          <button
            onClick={() => { setImportType('products'); setSelectedFile(null); setResults(null); }}
            className={`px-6 py-3 rounded-lg font-medium transition ${
              importType === 'products'
                ? 'bg-teal-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            📦 Products
          </button>
        </div>
        
        {/* Google Sheets Sync for Products */}
        {importType === 'products' && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={syncFromGoogleSheets}
              disabled={syncing}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition disabled:opacity-50 flex items-center gap-2"
            >
              {syncing ? '⏳ Syncing...' : '🔄 Sync from Google Sheets'}
            </button>
          </div>
        )}
      </div>

      {/* Template Download */}
      <div className="bg-white rounded-lg border border-gray-300 p-6 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Step 1: Download Template</h3>
        <p className="text-sm text-gray-600 mb-4">
          Download the CSV template, fill in your data, and upload it below.
        </p>
        <button
          onClick={() => downloadTemplate(importType)}
          className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition"
        >
          📥 Download {importType.charAt(0).toUpperCase() + importType.slice(1)} Template
        </button>
      </div>

      {/* File Upload */}
      <div className="bg-white rounded-lg border border-gray-300 p-6 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Step 2: Upload CSV File</h3>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none p-2"
        />
        {selectedFile && (
          <p className="mt-2 text-sm text-green-600">
            ✓ File selected: {selectedFile.name}
          </p>
        )}
      </div>

      {/* Preview */}
      {csvPreview.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-300 p-6 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Preview (first 5 rows)</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  {Object.keys(csvPreview[0]).map(header => (
                    <th key={header} className="px-3 py-2 text-left font-medium text-gray-700 border border-gray-200">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {csvPreview.map((row, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    {Object.values(row).map((value, i) => (
                      <td key={i} className="px-3 py-2 border border-gray-200 text-gray-700">
                        {value}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-gray-500">
            Total rows in file: {csvPreview.length > 0 ? 'Many' : '0'} (showing first 5)
          </p>
        </div>
      )}

      {/* Import Button */}
      {selectedFile && (
        <div className="bg-white rounded-lg border border-gray-300 p-6 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Step 3: Import Data</h3>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="duplicateAction"
                value="skip"
                checked={duplicateAction === 'skip'}
                onChange={(e) => setDuplicateAction(e.target.value)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Skip duplicates</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="duplicateAction"
                value="update"
                checked={duplicateAction === 'update'}
                onChange={(e) => setDuplicateAction(e.target.value)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Update existing records</span>
            </label>
          </div>
        </div>
      )}

      {selectedFile && (
        <div className="flex justify-end mb-6">
          <button
            onClick={handleImport}
            disabled={importing}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition shadow-sm disabled:opacity-50"
          >
            {importing ? '⏳ Importing...' : '📤 Import Data'}
          </button>
        </div>
      )}

      {/* Results */}
      {normalizedResults && (
        <div className={`rounded-lg border p-6 ${
          resultErrors.length > 0 ? 'bg-yellow-50 border-yellow-300' : 'bg-green-50 border-green-300'
        }`}>
          <h3 className="text-lg font-semibold mb-4">Import Results</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <div className="text-sm text-gray-600">Total</div>
              <div className="text-2xl font-bold">{normalizedResults.total}</div>
            </div>
            <div>
              <div className="text-sm text-green-600">Created</div>
              <div className="text-2xl font-bold text-green-600">{normalizedResults.created}</div>
            </div>
            <div>
              <div className="text-sm text-blue-600">Updated</div>
              <div className="text-2xl font-bold text-blue-600">{normalizedResults.updated}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Skipped</div>
              <div className="text-2xl font-bold text-gray-600">{normalizedResults.skipped}</div>
            </div>
          </div>

          {resultErrors.length > 0 && (
            <div>
              <h4 className="font-semibold text-red-700 mb-2">Errors ({resultErrors.length}):</h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {resultErrors.map((err, i) => (
                  <div key={i} className="text-sm bg-white p-2 rounded border border-red-200">
                    {typeof err === 'string' ? err : (
                      <>
                        <strong>Row {err.row}:</strong> {err.error}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}

export default SettingsImportData;
